<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import FolderTree from "@/ui/components/FileBrowser/FolderTree.vue";
import { useFileBrowserActions } from "@/ui/components/FileBrowser/useFileBrowserActions";
import {
	FolderNode,
	folderKey,
	isRootNode,
	isSharedWithMeNode,
} from "@/ui/components/FileBrowser/types";
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

/**
 * The folder tree, following the shape of rule_engine/GroupTree: native
 * <details>/<summary> for the disclosure, an explicit `level` driving the
 * indent, and a CSS-rotated chevron. Nothing to fight, and the expansion state
 * stays controlled by the caller so navigating can open the tree.
 */
const props = withDefaults(
	defineProps<{
		node: FolderNode;
		selected: ResourceTreeNodeDO | null;
		expandedKeys: string[];
		level?: number;
		isDropTarget: (folder: ResourceTreeNodeDO) => boolean;
	}>(),
	{ level: 0 }
);

const emit = defineEmits<{
	select: [folder: ResourceTreeNodeDO];
	toggle: [key: string];
	edit: [folder: ResourceTreeNodeDO];
	remove: [folder: ResourceTreeNodeDO];
	"drag-folder": [event: DragEvent, folder: ResourceTreeNodeDO];
	"drag-over-folder": [event: DragEvent, folder: ResourceTreeNodeDO];
	"drag-leave-folder": [folder: ResourceTreeNodeDO];
	"drop-on-folder": [event: DragEvent, folder: ResourceTreeNodeDO];
	"drag-end": [];
}>();

const { t } = useI18n();
const { hasAction } = useFileBrowserActions();

const nodeKey = computed(() => folderKey(props.node));

/** "Shared with me" is a flat bucket, so it never has children to disclose. */
const children = computed(() =>
	isSharedWithMeNode(props.node) ? [] : props.node.children ?? []
);

const isExpanded = computed(() => props.expandedKeys.includes(nodeKey.value));

const isSelected = computed(
	() => !!props.selected && folderKey(props.selected) === nodeKey.value
);

const icon = computed(() => {
	if (isSharedWithMeNode(props.node)) {
		return "fa: fa-duotone fa-rectangle-history-circle-user";
	}

	if (isRootNode(props.node)) {
		return "fa: fa-duotone fa-folder-tree";
	}

	return children.value.length && isExpanded.value
		? "mdi-folder-open"
		: "mdi-folder";
});

/** Root and the synthetic shared bucket are not the user's to rename or delete. */
const isEditable = computed(
	() => !isSharedWithMeNode(props.node) && !isRootNode(props.node)
);

/**
 * Reparenting a folder is the `update-folder` action, so a browser that was not
 * granted it gets a tree that does not drag rather than a move that 501s.
 */
const isDraggable = computed(
	() => isEditable.value && hasAction("update-folder")
);
</script>

<template>
	<details class="folder-node" :open="isExpanded">
		<summary
			class="node"
			:class="{
				selected: isSelected,
				'node--drop-target': isDropTarget(node),
			}"
			:style="{ paddingLeft: `${level * 0.75 + 0.25}rem` }"
			:draggable="isDraggable"
			@click.prevent="emit('select', node)"
			@dragstart.stop="emit('drag-folder', $event, node)"
			@dragend="emit('drag-end')"
			@dragenter="emit('drag-over-folder', $event, node)"
			@dragover="emit('drag-over-folder', $event, node)"
			@dragleave="emit('drag-leave-folder', node)"
			@drop="emit('drop-on-folder', $event, node)"
		>
			<span
				v-if="children.length"
				class="twisty"
				@click.stop.prevent="emit('toggle', nodeKey)"
			>
				<v-icon size="11">fa: fa-solid fa-chevron-right</v-icon>
			</span>
			<span v-else class="twisty-spacer" />

			<v-icon size="13" class="node-icon">{{ icon }}</v-icon>

			<span class="node-name">{{ node.name }}</span>

			<span v-if="isEditable" class="node-actions">
				<v-btn
					v-if="hasAction('update-folder')"
					icon
					size="17"
					variant="text"
					@click.stop.prevent="emit('edit', node)"
				>
					<v-icon size="10">fa: fa-light fa-pen</v-icon>
					<v-tooltip activator="parent">
						{{ t("fileBrowser.actions.renameOrMove") }}
					</v-tooltip>
				</v-btn>
				<v-btn
					v-if="hasAction('delete-folder')"
					icon
					size="17"
					variant="text"
					color="error"
					@click.stop.prevent="emit('remove', node)"
				>
					<v-icon size="10">fa: fa-light fa-trash</v-icon>
					<v-tooltip activator="parent">
						{{ t("fileBrowser.actions.deleteFolder") }}
					</v-tooltip>
				</v-btn>
			</span>
		</summary>

		<folder-tree
			v-for="child in children"
			:key="folderKey(child)"
			:node="child"
			:selected="selected"
			:expanded-keys="expandedKeys"
			:level="level + 1"
			:is-drop-target="isDropTarget"
			@select="emit('select', $event)"
			@toggle="emit('toggle', $event)"
			@edit="emit('edit', $event)"
			@remove="emit('remove', $event)"
			@drag-folder="(event, folder) => emit('drag-folder', event, folder)"
			@drag-over-folder="
				(event, folder) => emit('drag-over-folder', event, folder)
			"
			@drag-leave-folder="emit('drag-leave-folder', $event)"
			@drop-on-folder="(event, folder) => emit('drop-on-folder', event, folder)"
			@drag-end="emit('drag-end')"
		/>
	</details>
</template>

<style scoped lang="scss">
.node {
	display: flex;
	align-items: center;
	gap: 6px;
	min-height: 24px;
	padding-right: 4px;
	border-radius: 4px;
	cursor: pointer;
	user-select: none;
	list-style: none;
	font-size: 13px;
	color: rgb(var(--color-on-surface));
	transition: background-color 120ms ease;

	&::-webkit-details-marker {
		display: none;
	}

	&:focus {
		outline: none;
	}

	&:focus-visible {
		outline: 2px solid rgb(var(--color-primary));
		outline-offset: -2px;
	}

	&:hover {
		background-color: rgba(var(--color-secondary), 0.6);
	}

	&.selected {
		background-color: rgb(var(--color-secondary));
		font-weight: 600;
	}

	&--drop-target {
		outline: 2px solid rgb(var(--color-primary));
		outline-offset: -2px;
		background-color: rgba(var(--color-primary), 0.08);
	}
}

.twisty {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 12px;
	flex: none;
	opacity: 0.55;
	transition: transform 150ms ease;

	&:hover {
		opacity: 1;
	}
}

details[open] > .node > .twisty {
	transform: rotate(90deg);
}

.twisty-spacer {
	display: inline-block;
	width: 12px;
	flex: none;
}

.node-icon {
	flex: none;
	opacity: 0.7;
}

.node-name {
	flex: 1 1 auto;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.node-actions {
	display: flex;
	gap: 1px;
	margin-left: auto;
	padding-left: 4px;
	visibility: hidden;
	opacity: 0;
	transition: opacity 150ms ease;
}

.node:hover .node-actions {
	visibility: visible;
	opacity: 1;
}
</style>
